/**
 * Invitations page (/invitations, under Users in the sidebar).
 *
 * Shows the invitations the caller can see (their primary org + the ones they sent).
 * Clicking a row opens a details dialog with everything the API returns.
 * Resend / Edit / Revoke act on `invitationNumber` and only while the backend status is
 * `pending` — including lazily-expired rows, which display as "Expired" (spec §8.1).
 * Accept calls `accept-by-email` (no token) to accept on the invitee's behalf.
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/contexts/ToastContext'
import { Link } from '@tanstack/react-router'
import { Search, Edit, Trash2, Mail, Loader2, Send, CheckCircle2 } from 'lucide-react'
import { InviteUserComposer } from '@/forms/users'
import { InvitationStatusBadge } from './InvitationStatusBadge'
import { invitationDisplayStatus, isInvitationActionable } from '@/lib/invitations'
import { INVITATION_STATUS_VARIANTS, type Invitation, type InvitationStatus, type InvitationType } from '@/types/users'

const ITEMS_PER_PAGE = 10
const fmtDate = (s?: number | null) => (s ? new Date(s * 1000).toLocaleDateString() : '—')
const fmtDateTime = (s?: number | null) => (s ? new Date(s * 1000).toLocaleString() : '—')

export function InvitationList() {
  const { toast } = useToast()
  const { data, isLoading, isError, refetch } = useApiQuery('getInvitationList', {}, {})
  const { data: typesData } = useApiQuery('getInvitationTypes', { includeArchived: 'true' }, {})
  const { data: orgsData } = useApiQuery('getOrganizations', {}, {})
  const { data: usersData } = useApiQuery('getUsers', {}, {})

  const invitations: Invitation[] = (data as any)?.content?.invitations ?? []
  const types: InvitationType[] = (typesData as any)?.content?.invitationTypes ?? []
  const typeLabel = (name: string) => types.find((t) => t.invitationTypeName === name)?.invitationTypeLabel ?? name
  const orgName = (ref: string) =>
    ((orgsData as any)?.content?.organizations ?? []).find((o: any) => String(o.orgNumber) === ref)?.name ?? ref
  const targetLabel = (inv: Invitation) =>
    inv.targetKind === 'organization' && inv.targetReference ? orgName(inv.targetReference) : '—'
  const inviterName = (userNumber: number) => {
    const u = ((usersData as any)?.content?.users ?? []).find((x: any) => Number(x.userNumber) === Number(userNumber))
    return u ? `${u.firstName} ${u.lastName}` : String(userNumber)
  }

  const resendMut = useApiMutation('resendInvitation', 'POST', { invalidates: ['getInvitationList'] })
  const acceptMut = useApiMutation('acceptInvitationByEmail', 'POST', { invalidates: ['getInvitationList', 'getUsers'] })
  const revokeMut = useApiMutation('revokeInvitation', 'DELETE', { invalidates: ['getInvitationList'] })

  const [inviting, setInviting] = React.useState(false)
  const [editing, setEditing] = React.useState<Invitation | null>(null)
  const [viewing, setViewing] = React.useState<Invitation | null>(null)
  const [revoking, setRevoking] = React.useState<Invitation | null>(null)
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [typeFilter, setTypeFilter] = React.useState('all')
  const [page, setPage] = React.useState(1)
  React.useEffect(() => { setPage(1) }, [search, statusFilter, typeFilter])

  const handleResend = async (inv: Invitation) => {
    try {
      await resendMut.mutateAsync({ invitationNumber: inv.invitationNumber })
      toast({ title: 'Invitation resent', description: `Invitation resent to ${inv.emailId}. It now expires in 7 days.`, type: 'success' })
    } catch (e) {
      toast({ title: 'Could not resend invitation', description: (e as Error)?.message, type: 'error' })
    }
  }

  const handleAccept = async (inv: Invitation) => {
    try {
      const res = await acceptMut.mutateAsync({ email: inv.emailId })
      const created = res?.content?.user?.created
      toast({ title: 'Invitation accepted', description: `${inv.emailId} accepted${created ? ' — new user created' : ''}.`, type: 'success' })
    } catch (e) {
      toast({ title: 'Could not accept invitation', description: (e as Error)?.message, type: 'error' })
    }
  }

  const handleRevoke = async () => {
    if (!revoking) return
    try {
      // 204 No Content → the API layer resolves to {} — any non-throw is success.
      await revokeMut.mutateAsync({ invitationNumber: revoking.invitationNumber })
      toast({ title: 'Invitation withdrawn', type: 'success' })
    } catch (e) {
      toast({ title: 'Could not withdraw invitation', description: (e as Error)?.message, type: 'error' })
    }
    setRevoking(null)
  }

  if (inviting) {
    return <InviteUserComposer onSuccess={() => setInviting(false)} onCancel={() => setInviting(false)} />
  }

  if (editing) {
    return (
      <InviteUserComposer
        invitation={editing}
        targetLabel={targetLabel(editing)}
        onSuccess={() => setEditing(null)}
        onCancel={() => setEditing(null)}
      />
    )
  }

  if (isLoading) {
    return <Card className="shadow-sm"><CardContent className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></CardContent></Card>
  }
  if (isError) {
    return <Card className="shadow-sm"><CardContent className="flex flex-col items-center justify-center py-12 gap-4"><p className="text-destructive">Failed to load invitations</p><Button variant="outline" onClick={() => refetch()}>Try Again</Button></CardContent></Card>
  }

  const q = search.trim().toLowerCase()
  const filtered = invitations.filter((inv) => {
    const matchesSearch = !q || [inv.firstName, inv.lastName, `${inv.firstName} ${inv.lastName}`, inv.emailId, inv.mobileNumber]
      .some((f) => (f ?? '').toLowerCase().includes(q))
    const matchesStatus = statusFilter === 'all' || invitationDisplayStatus(inv) === statusFilter
    const matchesType = typeFilter === 'all' || inv.invitationType === typeFilter
    return matchesSearch && matchesStatus && matchesType
  })
  const anyFilter = !!q || statusFilter !== 'all' || typeFilter !== 'all'
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  const detailType = viewing ? types.find((t) => t.invitationTypeName === viewing.invitationType) : undefined
  const detail = (label: string, value: React.ReactNode) => (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm break-words">{value}</div>
    </div>
  )

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Send className="h-5 w-5 text-primary" /></div>
              <div><CardTitle className="text-xl">Invitations</CardTitle><CardDescription className="text-base">Track, resend and withdraw invitations</CardDescription></div>
            </div>
            <Button onClick={() => setInviting(true)} className="w-full sm:w-auto"><Send className="h-4 w-4 mr-2" />Invite User</Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search by name, email or mobile..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[220px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(Object.keys(INVITATION_STATUS_VARIANTS) as InvitationStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>{INVITATION_STATUS_VARIANTS[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[220px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map((t) => <SelectItem key={t.invitationTypeName} value={t.invitationTypeName}>{t.invitationTypeLabel}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Target</TableHead>
                <TableHead className="font-semibold text-center">Status</TableHead>
                <TableHead className="font-semibold">Invited by</TableHead>
                <TableHead className="font-semibold">Expires</TableHead>
                <TableHead className="w-[130px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.length > 0 ? paginated.map((inv) => (
                <TableRow key={inv.invitationNumber} className="cursor-pointer hover:bg-muted/50" onClick={() => setViewing(inv)}>
                  <TableCell className="font-medium">
                    <button type="button" className="text-primary hover:underline text-left" onClick={(e) => { e.stopPropagation(); setViewing(inv) }}>
                      {inv.firstName} {inv.lastName}
                    </button>
                  </TableCell>
                  <TableCell>{inv.emailId}</TableCell>
                  <TableCell>{targetLabel(inv)}</TableCell>
                  <TableCell className="text-center"><InvitationStatusBadge status={invitationDisplayStatus(inv)} /></TableCell>
                  <TableCell className="text-muted-foreground">{inviterName(inv.inviterUserNumber)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(inv.expiresAt)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {isInvitationActionable(inv) && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Resend" onClick={() => handleResend(inv)} disabled={resendMut.isPending}>
                          <Mail className="h-4 w-4" /><span className="sr-only">Resend to {inv.emailId}</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Accept" onClick={() => handleAccept(inv)} disabled={acceptMut.isPending}>
                          <CheckCircle2 className="h-4 w-4" /><span className="sr-only">Accept invitation for {inv.emailId}</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => setEditing(inv)}>
                          <Edit className="h-4 w-4" /><span className="sr-only">Edit invitation for {inv.emailId}</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive" title="Withdraw" onClick={() => setRevoking(inv)}>
                          <Trash2 className="h-4 w-4" /><span className="sr-only">Withdraw invitation for {inv.emailId}</span>
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  {anyFilter ? 'No invitations match your filters.' : 'No invitations yet.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.firstName} {viewing.lastName}</DialogTitle>
                <DialogDescription>Invitation #{viewing.invitationNumber}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                {detail('Status', <InvitationStatusBadge status={invitationDisplayStatus(viewing)} />)}
                {detail('Email', viewing.emailId)}
                {detail('Mobile', viewing.mobileNumber || '—')}
                {detail('Type', typeLabel(viewing.invitationType))}
                {detail('Organization', targetLabel(viewing))}
                {detail('Invited by', inviterName(viewing.inviterUserNumber))}
                {detail('Sent', fmtDateTime(viewing.createdAt))}
                {detail('Expires', fmtDateTime(viewing.expiresAt))}
                {viewing.acceptedAt != null && detail('Accepted', fmtDateTime(viewing.acceptedAt))}
                {viewing.redeemedAt != null && detail('First sign-in', fmtDateTime(viewing.redeemedAt))}
                {viewing.declinedAt != null && detail('Declined', fmtDateTime(viewing.declinedAt))}
                {viewing.acceptedUserNumber != null && detail('User', (
                  <Link to="/users/$userNumber" params={{ userNumber: String(viewing.acceptedUserNumber) }} className="text-primary hover:underline">
                    View user
                  </Link>
                ))}
                {detail('Last updated', fmtDateTime(viewing.updatedAt))}
              </div>
              {detailType?.invitationTypeDescription && (
                <p className="text-sm text-muted-foreground border-t pt-3">{detailType.invitationTypeDescription}</p>
              )}
              {isInvitationActionable(viewing) && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => { const v = viewing; setViewing(null); setRevoking(v) }}>Withdraw</Button>
                  <Button variant="outline" onClick={() => { const v = viewing; setViewing(null); setEditing(v) }}>Edit</Button>
                  <Button variant="outline" onClick={() => { const v = viewing; setViewing(null); handleAccept(v) }} disabled={acceptMut.isPending}>Accept</Button>
                  <Button onClick={() => { const v = viewing; setViewing(null); handleResend(v) }} disabled={resendMut.isPending}>Resend</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Withdraw this invitation?</DialogTitle>
            <DialogDescription>
              The link sent to {revoking?.emailId} will stop working. This can't be undone — invite them again if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoking(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={revokeMut.isPending}>Withdraw</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
